import { useState, useEffect } from 'react';
import { getAlerts, createAlert, updateAlert, deleteAlert, getClients, getAvailableContracts, checkAlerts } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Plus, Bell, Search, ChevronDown, ChevronRight, Pencil, Trash2, Zap, Flame, Building2, RefreshCw, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const AlertsPage = () => {
  const { isAdmin } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [search, setSearch] = useState('');
  const [energyFilter, setEnergyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedClients, setExpandedClients] = useState({});
  const [formData, setFormData] = useState({
    client_id: '', energy_type: 'electricity', contract_name: '', min_price: '', max_price: '',
    deadline_date: '', deadline_reminder_days: '1'
  });
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [alertsRes, clientsRes, contractsRes] = await Promise.all([
        getAlerts(),
        isAdmin ? getClients() : Promise.resolve({ data: [] }),
        getAvailableContracts()
      ]);
      setAlerts(alertsRes.data);
      setClients(clientsRes.data);
      setContracts(contractsRes.data);
      // Expand all clients by default
      const expanded = {};
      alertsRes.data.forEach(a => { expanded[a.client_id] = true; });
      setExpandedClients(expanded);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const contractInfo = contracts.find(c => c.name === formData.contract_name);
    try {
      if (editingAlert) {
        await updateAlert(editingAlert.id, {
          min_price: parseFloat(formData.min_price),
          max_price: parseFloat(formData.max_price),
          deadline_date: formData.deadline_date || null,
          deadline_reminder_days: formData.deadline_reminder_days ? parseInt(formData.deadline_reminder_days) : 7
        });
        toast.success('Alerte mise à jour !');
      } else {
        await createAlert({
          ...formData,
          contract_type: contractInfo?.contract_type || 'unknown',
          min_price: parseFloat(formData.min_price),
          max_price: parseFloat(formData.max_price),
          deadline_date: formData.deadline_date || null,
          deadline_reminder_days: formData.deadline_reminder_days ? parseInt(formData.deadline_reminder_days) : 7
        });
        toast.success('Alerte créée !');
      }
      setDialogOpen(false);
      setEditingAlert(null);
      setFormData({ client_id: '', energy_type: 'electricity', contract_name: '', min_price: '', max_price: '', deadline_date: '', deadline_reminder_days: '7' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (alert) => {
    setEditingAlert(alert);
    setFormData({
      client_id: alert.client_id,
      energy_type: alert.energy_type,
      contract_name: alert.contract_name,
      min_price: alert.min_price.toString(),
      max_price: alert.max_price.toString(),
      deadline_date: alert.deadline_date || '',
      deadline_reminder_days: (alert.deadline_reminder_days || 7).toString()
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette alerte ?')) return;
    try {
      await deleteAlert(id);
      toast.success('Alerte supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCheckAlerts = async () => {
    setChecking(true);
    try {
      const res = await checkAlerts();
      const { triggered } = res.data;
      if (triggered > 0) {
        toast.success(`${triggered} nouveau(x) seuil(s) franchi(s) — consultez "Alertes à envoyer"`);
      } else {
        toast.info('Aucun seuil atteint pour le moment');
      }
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la vérification');
    } finally {
      setChecking(false);
    }
  };

  const toggleClient = (clientId) => {
    setExpandedClients(prev => ({ ...prev, [clientId]: !prev[clientId] }));
  };

  // Filter alerts
  const filteredAlerts = alerts.filter(a => {
    if (energyFilter !== 'all' && a.energy_type !== energyFilter) return false;
    if (statusFilter === 'active' && (a.triggered_at || !a.is_active)) return false;
    if (statusFilter === 'triggered' && !a.triggered_at) return false;
    if (search) {
      const s = search.toLowerCase();
      return (a.client_name || '').toLowerCase().includes(s) || (a.contract_name || '').toLowerCase().includes(s);
    }
    return true;
  });

  // Group by client
  const grouped = {};
  filteredAlerts.forEach(a => {
    const key = a.client_id;
    if (!grouped[key]) {
      grouped[key] = { client_name: a.client_name || 'Client', client_id: key, alerts: [] };
    }
    grouped[key].alerts.push(a);
  });
  const groupedList = Object.values(grouped).sort((a, b) => a.client_name.localeCompare(b.client_name));

  // Available contracts for form
  const availableContracts = contracts.filter(c => c.energy_type === formData.energy_type);

  // Stats
  const activeCount = alerts.filter(a => a.is_active && !a.triggered_at).length;
  const triggeredCount = alerts.filter(a => a.triggered_at).length;

  if (loading) {
    return (
      <div className="space-y-4" data-testid="alerts-loading">
        <Skeleton className="h-10 w-48 bg-white" />
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 bg-white" />)}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="alerts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#191D1E]">
            {isAdmin ? 'Gestion des alertes' : 'Mes alertes'}
          </h1>
          <p className="text-sm text-[#687175] mt-1">
            {alerts.length} alerte(s) &middot; {activeCount} active(s) &middot; {triggeredCount} déclenchée(s)
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleCheckAlerts} disabled={checking} className="btn-secondary" data-testid="check-alerts-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} /> Vérifier les seuils
            </Button>
            <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) { setEditingAlert(null); setFormData({ client_id: '', energy_type: 'electricity', contract_name: '', min_price: '', max_price: '' }); }
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-alert-btn">
                <Plus className="w-4 h-4 mr-2" /> Nouvelle alerte
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#BFC6CC]/12">
              <DialogHeader>
                <DialogTitle className="text-[#191D1E]">
                  {editingAlert ? 'Modifier l\'alerte' : 'Nouvelle alerte'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {!editingAlert && (
                  <>
                    <div>
                      <Label className="text-[#191D1E] text-sm font-medium">Client</Label>
                      <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })}>
                        <SelectTrigger className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" data-testid="alert-client-select">
                          <SelectValue placeholder="Sélectionnez un client" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-[#BFC6CC]">
                          {clients.map(client => (
                            <SelectItem key={client.id} value={client.id} className="text-[#191D1E]">
                              {client.name} {client.company ? `- ${client.company}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#191D1E] text-sm font-medium">Type d'énergie</Label>
                      <Select value={formData.energy_type} onValueChange={(v) => setFormData({ ...formData, energy_type: v, contract_name: '' })}>
                        <SelectTrigger className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" data-testid="alert-energy-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-[#BFC6CC]">
                          <SelectItem value="electricity" className="text-[#191D1E]">Électricité</SelectItem>
                          <SelectItem value="gas" className="text-[#191D1E]">Gaz Naturel</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#191D1E] text-sm font-medium">Contrat</Label>
                      <Select value={formData.contract_name} onValueChange={(v) => setFormData({ ...formData, contract_name: v })}>
                        <SelectTrigger className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" data-testid="alert-contract-select">
                          <SelectValue placeholder="Sélectionnez un contrat" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-[#BFC6CC]">
                          {availableContracts.map(c => (
                            <SelectItem key={c.name} value={c.name} className="text-[#191D1E]">
                              {c.name} — {c.latest_price?.toFixed(2)} €/MWh
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#191D1E] text-sm font-medium">Prix min (€/MWh)</Label>
                    <Input type="number" step="0.01" value={formData.min_price}
                      onChange={(e) => setFormData({ ...formData, min_price: e.target.value })}
                      className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" placeholder="40.00" required
                      data-testid="alert-min-price-input" />
                  </div>
                  <div>
                    <Label className="text-[#191D1E] text-sm font-medium">Prix max (€/MWh)</Label>
                    <Input type="number" step="0.01" value={formData.max_price}
                      onChange={(e) => setFormData({ ...formData, max_price: e.target.value })}
                      className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" placeholder="65.00" required
                      data-testid="alert-max-price-input" />
                  </div>
                </div>
                
                {/* Date limite de décision */}
                <div className="p-3 rounded-xl bg-[#F5F6F9] border border-[#BFC6CC]/20 space-y-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#397DFF]" />
                    <Label className="text-[#191D1E] text-sm font-medium">Date limite de décision (optionnel)</Label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[#687175] text-xs">Date limite</Label>
                      <Input type="date" value={formData.deadline_date}
                        onChange={(e) => setFormData({ ...formData, deadline_date: e.target.value })}
                        className="mt-1 bg-white border-[#BFC6CC] text-[#191D1E]"
                        data-testid="alert-deadline-date-input" />
                    </div>
                    <div>
                      <Label className="text-[#687175] text-xs">Rappel (mois avant)</Label>
                      <Input type="number" min="1" max="24" value={formData.deadline_reminder_days}
                        onChange={(e) => setFormData({ ...formData, deadline_reminder_days: e.target.value })}
                        className="mt-1 bg-white border-[#BFC6CC] text-[#191D1E]" placeholder="1"
                        data-testid="alert-deadline-reminder-input" />
                    </div>
                  </div>
                </div>

                <Button type="submit" className="w-full btn-primary" disabled={submitting} data-testid="alert-submit-btn">
                  {submitting ? 'Enregistrement...' : editingAlert ? 'Mettre à jour' : 'Créer l\'alerte'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Filters bar */}
      <div className="flex flex-col sm:flex-row gap-3 bg-white rounded-2xl border border-[#BFC6CC]/12 p-4">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#939BA0]" />
          <Input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un client ou contrat..."
            className="pl-9 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E] placeholder-[#939BA0]"
            data-testid="alert-search-input"
          />
        </div>
        <div className="flex gap-2">
          {[
            { val: 'all', label: 'Toutes' },
            { val: 'active', label: 'Actives' },
            { val: 'triggered', label: 'Déclenchées' },
          ].map(f => (
            <button key={f.val} onClick={() => setStatusFilter(f.val)}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                statusFilter === f.val ? 'bg-[#397DFF] text-white' : 'bg-[#F5F6F9] text-[#687175] hover:bg-[#E5E7EB]'
              }`} data-testid={`status-filter-${f.val}`}>
              {f.label}
            </button>
          ))}
          <div className="w-px bg-[#BFC6CC]/30 mx-1" />
          {[
            { val: 'all', label: 'Tout' },
            { val: 'electricity', label: 'Élec.' },
            { val: 'gas', label: 'Gaz' },
          ].map(f => (
            <button key={f.val} onClick={() => setEnergyFilter(f.val)}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                energyFilter === f.val ? 'bg-[#397DFF] text-white' : 'bg-[#F5F6F9] text-[#687175] hover:bg-[#E5E7EB]'
              }`} data-testid={`energy-filter-${f.val}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grouped alerts */}
      {groupedList.length === 0 ? (
        <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-12 text-center">
          <Bell className="w-12 h-12 text-[#BFC6CC] mx-auto mb-3" />
          <p className="text-[#687175]">Aucune alerte trouvée</p>
        </div>
      ) : (
        <div className="space-y-3">
          {groupedList.map(group => (
            <div key={group.client_id} className="bg-white rounded-2xl border border-[#BFC6CC]/12 overflow-hidden" data-testid={`alert-group-${group.client_id}`}>
              {/* Client header */}
              <button
                onClick={() => toggleClient(group.client_id)}
                className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-[#F5F6F9] transition-colors text-left"
                data-testid={`toggle-client-${group.client_id}`}
              >
                <div className="w-8 h-8 rounded-lg bg-[#397DFF]/10 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4 h-4 text-[#397DFF]" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-[#191D1E] text-sm">{group.client_name}</span>
                </div>
                <span className="text-xs font-medium text-[#687175] bg-[#F5F6F9] px-2.5 py-1 rounded-full">
                  {group.alerts.length} alerte{group.alerts.length > 1 ? 's' : ''}
                </span>
                {expandedClients[group.client_id] ? (
                  <ChevronDown className="w-4 h-4 text-[#939BA0]" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-[#939BA0]" />
                )}
              </button>

              {/* Alert rows */}
              <AnimatePresence>
                {expandedClients[group.client_id] && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="overflow-x-auto">
                    <table className="w-full min-w-[560px]">
                      <thead>
                        <tr className="border-t border-[#BFC6CC]/20 bg-[#F5F6F9]">
                          <th className="px-5 py-2 text-left text-xs font-medium text-[#687175]">Contrat</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-[#687175]">Type</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-[#687175]">Prix min</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-[#687175]">Prix max</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-[#687175]">Statut</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-[#687175]">Date limite</th>
                          {isAdmin && <th className="px-3 py-2 text-right text-xs font-medium text-[#687175]">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {group.alerts.map((alert, idx) => {
                          const isGas = alert.energy_type === 'gas';
                          const isTriggered = alert.triggered_at !== null;
                          return (
                            <tr key={alert.id}
                              className={`border-t border-[#BFC6CC]/10 hover:bg-[#F5F6F9]/50 transition-colors ${idx % 2 === 0 ? '' : 'bg-[#F5F6F9]/30'}`}
                              data-testid={`alert-row-${alert.id}`}
                            >
                              <td className="px-5 py-3">
                                <div className="flex items-center gap-2">
                                  {isGas ? (
                                    <Flame className="w-3.5 h-3.5 text-[#397DFF] flex-shrink-0" />
                                  ) : (
                                    <Zap className="w-3.5 h-3.5 text-[#F9C80E] flex-shrink-0" />
                                  )}
                                  <span className="text-sm font-medium text-[#191D1E]">{alert.contract_name}</span>
                                </div>
                              </td>
                              <td className="px-3 py-3">
                                <span className="text-xs text-[#687175]">{isGas ? 'Gaz' : 'Élec.'}</span>
                              </td>
                              <td className="px-3 py-3 text-right">
                                <span className="font-mono text-sm font-semibold text-emerald-600">{alert.min_price} €</span>
                              </td>
                              <td className="px-3 py-3 text-right">
                                <span className="font-mono text-sm font-semibold text-red-500">{alert.max_price} €</span>
                              </td>
                              <td className="px-3 py-3 text-center">
                                {!alert.is_active ? (
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-[#BFC6CC]/20 text-[#687175]">Inactive</span>
                                ) : isTriggered ? (
                                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                    alert.trigger_type === 'min' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
                                  }`}>
                                    {alert.trigger_type === 'min' ? 'Min atteint' : 'Max atteint'}
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-[#397DFF]/10 text-[#397DFF] font-medium">Active</span>
                                )}
                              </td>
                              <td className="px-3 py-3 text-center">
                                {alert.deadline_date ? (() => {
                                  const dl = new Date(alert.deadline_date);
                                  const now = new Date();
                                  const daysLeft = Math.ceil((dl - now) / (1000 * 60 * 60 * 24));
                                  const isPast = daysLeft < 0;
                                  const totalMonths = (dl.getFullYear() - now.getFullYear()) * 12 + (dl.getMonth() - now.getMonth());
                                  const reminderMonths = alert.deadline_reminder_days || 1;
                                  const isUrgent = totalMonths <= reminderMonths;
                                  return (
                                    <div className="flex flex-col items-center">
                                      <span className={`text-xs font-medium ${isPast ? 'text-red-600' : isUrgent ? 'text-orange-500' : 'text-[#687175]'}`}>
                                        {dl.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                      </span>
                                    </div>
                                  );
                                })() : (
                                  <span className="text-xs text-[#939BA0]">—</span>
                                )}
                              </td>
                              {isAdmin && (
                                <td className="px-3 py-3 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <button onClick={() => handleEdit(alert)}
                                      className="p-1.5 text-[#939BA0] hover:text-[#397DFF] hover:bg-[#397DFF]/10 rounded-md transition-colors"
                                      data-testid={`edit-alert-${alert.id}`}>
                                      <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                    <button onClick={() => handleDelete(alert.id)}
                                      className="p-1.5 text-[#939BA0] hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                                      data-testid={`delete-alert-${alert.id}`}>
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AlertsPage;
